/**
 * Contrato de atualização entre a página e o Service Worker.
 *
 * `registerType: 'prompt'`: a versão nova instala e fica em `waiting` até a
 * página mandar promovê-la — pelo botão "Nova versão pronta · Atualizar" ou
 * na abertura do app, quando já há uma em espera (`register.ts`). Quem manda
 * é `updateSW(true)` do vite-plugin-pwa, que delega a `Workbox.messageSkipWaiting()`
 * do workbox-window: a mensagem é `{ type: 'SKIP_WAITING' }`.
 *
 * O SW respondia só a `NV_SKIP_WAITING`, um nome interno que nenhum código da
 * página envia. Resultado, provado em staging (14/09/2026): com um cliente
 * aberto, a versão nova nunca saía de `waiting`; no celular "funcionava" só
 * porque o app é fechado por completo. `NV_SKIP_WAITING` continua aceito por
 * compatibilidade com qualquer bundle anterior que ainda esteja aberto.
 *
 * Função pura, sem `self`: a decisão é testável fora do worker.
 */

const TIPOS_QUE_PROMOVEM = new Set(['SKIP_WAITING', 'NV_SKIP_WAITING']);

/** A mensagem recebida pede que o worker em espera assuma agora? */
export function deveAtivarAgora(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const type = (data as { type?: unknown }).type;
  return typeof type === 'string' && TIPOS_QUE_PROMOVEM.has(type);
}

/**
 * "Atualizar" com rede de segurança (#81). `updateSW(true)` só manda a
 * mensagem; quem recarrega é o vite-plugin-pwa, ao ouvir `controlling` com
 * `isUpdate`. No iPhone (15/09/2026), com o app aberto ~1 h, esse evento não
 * veio e a faixa ficou em "Atualizando…" até fechar o app. Se a recarga não
 * acontecer em `esperaMs`, recarrega mesmo assim: se o worker novo já assumiu,
 * a página volta nele; se ainda espera, a abertura o promove (`register.ts`).
 */
export async function aplicarComRedeDeSeguranca(
  enviar: () => Promise<void>,
  recarregar: () => void,
  esperaMs = 4000,
): Promise<void> {
  let feito = false;
  const umaVez = () => {
    if (feito) return;
    feito = true;
    recarregar();
  };
  setTimeout(umaVez, esperaMs);
  try {
    await enviar();
  } catch {
    umaVez();
  }
}
